import { ChangeDetectionStrategy, Component, computed, DestroyRef, inject, input, output } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { AiProposedActionComponent, ProposedActionApproval, ProposedActionRejection } from '@galaxy/ai-assistant';
import { SnackbarService } from '@vendasta/galaxy/snackbar-service';
import {
  approveProposedAction$,
  rejectProposedAction$,
} from '@vendasta/protos/ai_assistants/v1alpha1/session-service';
import { Namespace } from '@vendasta/protos/ai_assistants/v1alpha1';
import { GlobalParticipantTypeValue, ParticipantTypeValue } from '@vendasta/protos/conversation/v1/enums';
import { catchError, EMPTY, Observable, take } from 'rxjs';
import { ConversationMessage } from '../../../../../../../core/src';
import { ComposerFocusService } from '../../../composer-focus.service';

const PROPOSED_ACTION_THOUGHT_TYPE = 'proposed_action';
const SYSTEM_ASSISTANT_INTERNAL_ID = 'ASSISTANT-system';

interface ProposedActionContent {
  session_event_id: string;
  namespace_id: string;
  namespace_type: string;
}

interface ProposedActionUIModel {
  eventId: string;
  namespaceId: string;
  namespaceType: string;
}

type NamespaceSubject = {
  participantType?: number;
  internalParticipantId?: string;
};

type AssistantParticipant = {
  participantType?: number;
  internalParticipantId?: string;
  partnerId?: string;
  accountGroupId?: string;
  namespaceHierarchy?: ReadonlyArray<NamespaceSubject>;
};

@Component({
  selector: 'inbox-proposed-actions',
  templateUrl: './inbox-proposed-actions.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [AiProposedActionComponent],
})
export class InboxProposedActionsComponent {
  readonly message = input<ConversationMessage>();
  readonly conversationId = input.required<string>();
  readonly latestUserMessageTime = input<Date | undefined>(undefined);

  readonly resolvedMessage = output<string>();

  private readonly snackbarService = inject(SnackbarService);
  private readonly composerFocusService = inject(ComposerFocusService);
  private readonly destroyRef = inject(DestroyRef);

  protected readonly proposedActions = computed<ProposedActionUIModel[]>(() => {
    const thoughts = this.message()?.aiThoughts ?? [];
    const actions: ProposedActionUIModel[] = [];
    for (const thought of thoughts) {
      if (thought.thoughtType !== PROPOSED_ACTION_THOUGHT_TYPE) continue;
      const parsed = this.parseProposedActionContent(thought.content);
      if (parsed) {
        actions.push({
          eventId: parsed.session_event_id,
          namespaceId: parsed.namespace_id,
          namespaceType: parsed.namespace_type,
        });
      }
    }
    return actions;
  });

  protected readonly superseded = computed(() => {
    const userMessageTime = this.latestUserMessageTime();
    const messageTime = this.message()?.created;
    if (!userMessageTime || !messageTime) {
      return false;
    }
    return userMessageTime > messageTime;
  });

  protected onChangeRequested(): void {
    this.composerFocusService.requestFocus();
  }

  protected onApproved(event: ProposedActionApproval, actionComponent: AiProposedActionComponent): void {
    this.runAction(
      approveProposedAction$({
        eventId: event.eventId,
        sessionId: this.conversationId(),
        namespace: parseNamespace(this.message()?.sender),
      }),
      actionComponent,
      event.feedback,
    );
  }

  protected onRejected(event: ProposedActionRejection, actionComponent: AiProposedActionComponent): void {
    this.runAction(
      rejectProposedAction$({
        eventId: event.eventId,
        sessionId: this.conversationId(),
        namespace: parseNamespace(this.message()?.sender),
        feedback: event.feedback,
      }),
      actionComponent,
      event.feedback,
    );
  }

  private runAction(rpc$: Observable<unknown>, actionComponent: AiProposedActionComponent, feedback: string): void {
    rpc$
      .pipe(
        take(1),
        catchError(() => {
          actionComponent.resetActionInProgress();
          this.snackbarService.openErrorSnack('INBOX.SOMETHING_WENT_WRONG');
          return EMPTY;
        }),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe(() => {
        this.resolvedMessage.emit(feedback);
        actionComponent.reload();
      });
  }

  private parseProposedActionContent(rawContent: string | undefined): ProposedActionContent | null {
    try {
      const eventContent: { ProposedAction: ProposedActionContent } = JSON.parse(rawContent ?? '{}');
      return eventContent?.ProposedAction?.session_event_id ? eventContent.ProposedAction : null;
    } catch {
      return null;
    }
  }
}

function parseNamespace(participant: AssistantParticipant | undefined): Namespace {
  if (!participant) {
    return { systemNamespace: {} };
  }

  if (
    participant.participantType !== ParticipantTypeValue.PARTICIPANT_TYPE_AI_ASSISTANT &&
    participant.participantType !== ParticipantTypeValue.PARTICIPANT_TYPE_OPENAI_BOT
  ) {
    return { systemNamespace: {} };
  }

  if (participant.internalParticipantId === SYSTEM_ASSISTANT_INTERNAL_ID) {
    return { systemNamespace: {} };
  }

  const subject = resolveSubjectParticipant(participant);
  if (!subject) {
    return { systemNamespace: {} };
  }

  switch (subject.participantType) {
    case GlobalParticipantTypeValue.GLOBAL_PARTICIPANT_TYPE_ACCOUNT_GROUP:
      return { accountGroupNamespace: { accountGroupId: subject.internalParticipantId ?? '' } };
    case GlobalParticipantTypeValue.GLOBAL_PARTICIPANT_TYPE_PARTNER:
    case GlobalParticipantTypeValue.GLOBAL_PARTICIPANT_TYPE_VENDOR:
      return { partnerNamespace: { partnerId: subject.internalParticipantId ?? '' } };
    default:
      return { systemNamespace: {} };
  }
}

function resolveSubjectParticipant(participant: AssistantParticipant): NamespaceSubject | null {
  const hierarchy = participant.namespaceHierarchy ?? [];
  if (hierarchy.length > 0) {
    return hierarchy[hierarchy.length - 1];
  }
  if (participant.accountGroupId) {
    return {
      participantType: GlobalParticipantTypeValue.GLOBAL_PARTICIPANT_TYPE_ACCOUNT_GROUP,
      internalParticipantId: participant.accountGroupId,
    };
  }
  if (participant.partnerId) {
    return {
      participantType: GlobalParticipantTypeValue.GLOBAL_PARTICIPANT_TYPE_PARTNER,
      internalParticipantId: participant.partnerId,
    };
  }
  return null;
}
